import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { 
    HcpDelegationApi, 
    ICreateDelegationModel
} from '../apis/HcpDelegationApi/HcpDelegationApi';

// Query Keys
export const HCP_DELEGATION_QUERY_KEYS = {
    myDelegations: ['hcp-delegations', 'my-delegations'],
    patientDelegations: (patientId: number) => ['hcp-delegations', 'patient', patientId],
    availableHcps: ['hcp-delegations', 'available-hcps'],
};

// HCP Hooks
export const useMyDelegations = () => {
    return useQuery({
        queryKey: HCP_DELEGATION_QUERY_KEYS.myDelegations,
        queryFn: () => HcpDelegationApi.getMyDelegations(),
        select: (data) => data.delegations || [],
    });
};

export const useAvailableHcps = () => {
    return useQuery({
        queryKey: HCP_DELEGATION_QUERY_KEYS.availableHcps,
        queryFn: () => HcpDelegationApi.getAvailableHcps(),
        select: (data) => data.availableHcps || [],
    });
};

export const useCreateDelegation = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (request: ICreateDelegationModel) => HcpDelegationApi.createDelegation(request),
        onSuccess: (data) => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: HCP_DELEGATION_QUERY_KEYS.myDelegations });
            queryClient.invalidateQueries({ queryKey: ['hcp-delegations', 'patient'] });
            
            notifications.show({
                title: t('delegation.success.title'),
                message: data.message || t('delegation.success.created'),
                color: 'green',
            });
        },
        onError: (error: any) => {
            notifications.show({
                title: t('delegation.error.title'),
                message: error.message || t('delegation.error.createFailed'),
                color: 'red',
            });
        },
    });
};

// Patient/HCP Shared Hooks
export const usePatientDelegations = (patientId: number) => {
    return useQuery({
        queryKey: HCP_DELEGATION_QUERY_KEYS.patientDelegations(patientId),
        queryFn: () => HcpDelegationApi.getPatientDelegations(patientId),
        select: (data) => data.delegations || [],
        enabled: !!patientId,
    });
};

// Shared Hooks
export const useRevokeDelegation = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ delegationId, reason }: { delegationId: number; reason?: string }) => 
            HcpDelegationApi.revokeDelegation(delegationId, reason),
        onSuccess: () => {
            // Invalidate all delegation queries
            queryClient.invalidateQueries({ queryKey: ['hcp-delegations'] });
            
            notifications.show({
                title: t('delegation.success.title'),
                message: t('delegation.success.revoked'),
                color: 'green',
            });
        },
        onError: (error: any) => {
            notifications.show({
                title: t('delegation.error.title'),
                message: error.message || t('delegation.error.revokeFailed'),
                color: 'red',
            });
        },
    });
};
